// Hole shape outlines, generated at the origin then rotated and translated.
// All outlines are CCW (positive signed area).

import type { Ring, ShapeStyle } from './types'
import { circleRing, circleSegments, rotateRing, translateRing, ensureWinding } from './geometry'

// 'custom' is compiled separately (see core/customShape.ts); callers that can
// meet it pass the compiled silhouette instead. This function keeps returning
// a circle for it so no caller can crash on the enum growing.
export function shapeOutline(shape: ShapeStyle, size: number, tol: number): Ring {
  const r = size / 2
  switch (shape.kind) {
    case 'circle':
    case 'custom':
      return circleRing(0, 0, r, tol)
    case 'slot':
      return slotRing(size, Math.max(0.05, Math.min(0.95, shape.slotRatio)), tol)
    case 'polygon':
      return regularPolygon(Math.max(3, Math.round(shape.sides)), r)
    case 'star':
      return starRing(Math.max(3, Math.round(shape.starPoints)), r, Math.max(0.1, Math.min(0.95, shape.starInner)))
  }
}

// Capsule: overall length = size, width = size * ratio, long axis along x.
function slotRing(size: number, ratio: number, tol: number): Ring {
  const w = size * ratio
  const rr = w / 2
  const half = size / 2 - rr
  const segs = Math.max(4, Math.floor(circleSegments(rr, tol) / 2))
  const ring: Ring = []
  // Right cap, from -90 to +90 degrees.
  for (let i = 0; i <= segs; i++) {
    const a = -Math.PI / 2 + (i / segs) * Math.PI
    ring.push([half + rr * Math.cos(a), rr * Math.sin(a)])
  }
  // Left cap, from +90 to +270 degrees.
  for (let i = 0; i <= segs; i++) {
    const a = Math.PI / 2 + (i / segs) * Math.PI
    ring.push([-half + rr * Math.cos(a), rr * Math.sin(a)])
  }
  return ensureWinding(dedupe(ring), true)
}

function regularPolygon(sides: number, r: number): Ring {
  const ring: Ring = []
  for (let i = 0; i < sides; i++) {
    const a = (i / sides) * Math.PI * 2 - Math.PI / 2
    ring.push([r * Math.cos(a), r * Math.sin(a)])
  }
  return ensureWinding(ring, true)
}

function starRing(points: number, r: number, innerRatio: number): Ring {
  const ring: Ring = []
  const inner = r * innerRatio
  for (let i = 0; i < points * 2; i++) {
    const rad = i % 2 === 0 ? r : inner
    const a = (i / (points * 2)) * Math.PI * 2 - Math.PI / 2
    ring.push([rad * Math.cos(a), rad * Math.sin(a)])
  }
  return ensureWinding(ring, true)
}

function dedupe(ring: Ring): Ring {
  const out: Ring = []
  for (const p of ring) {
    const last = out[out.length - 1]
    if (last && Math.hypot(p[0] - last[0], p[1] - last[1]) < 1e-9) continue
    out.push(p)
  }
  const first = out[0]
  const last = out[out.length - 1]
  if (out.length > 1 && Math.hypot(first[0] - last[0], first[1] - last[1]) < 1e-9) out.pop()
  return out
}

export function placeOutline(outline: Ring, x: number, y: number, angleRad: number): Ring {
  return translateRing(rotateRing(outline, angleRad), x, y)
}

// Resample a closed ring to exactly n vertices, evenly spaced by arc length,
// starting at the original first vertex.
export function resampleByArcLength(ring: Ring, n: number): Ring {
  const m = ring.length
  const cum: number[] = [0]
  for (let i = 0; i < m; i++) {
    const [ax, ay] = ring[i]
    const [bx, by] = ring[(i + 1) % m]
    cum.push(cum[i] + Math.hypot(bx - ax, by - ay))
  }
  const total = cum[m]
  if (total < 1e-12) return Array.from({ length: n }, () => [ring[0][0], ring[0][1]] as [number, number])
  const out: Ring = []
  let seg = 0
  for (let k = 0; k < n; k++) {
    const s = (total * k) / n
    while (seg + 1 < cum.length - 1 && cum[seg + 1] <= s) seg++
    const [ax, ay] = ring[seg]
    const [bx, by] = ring[(seg + 1) % m]
    const span = cum[seg + 1] - cum[seg]
    const t = span > 1e-12 ? (s - cum[seg]) / span : 0
    out.push([ax + (bx - ax) * t, ay + (by - ay) * t])
  }
  return out
}

// Rotate the vertex array so index 0 sits nearest to angle 0 from the origin
// (outlines are origin-centered). Aligning both shapes' start vertices keeps
// morphs twist-free.
function alignStart(ring: Ring): Ring {
  let best = 0
  let bestScore = -Infinity
  for (let i = 0; i < ring.length; i++) {
    const [x, y] = ring[i]
    const score = x - Math.abs(y) * 1e-3
    if (score > bestScore) {
      bestScore = score
      best = i
    }
  }
  return [...ring.slice(best), ...ring.slice(0, best)]
}

// Morph between two rings: resample to a common vertex count by arc length,
// align start vertices, interpolate.
export function morphRings(ra: Ring, rb: Ring, t: number): Ring {
  const n = Math.max(ra.length, rb.length)
  const A = alignStart(resampleByArcLength(ra, n))
  const B = alignStart(resampleByArcLength(rb, n))
  const out: Ring = []
  for (let i = 0; i < n; i++) {
    out.push([A[i][0] + (B[i][0] - A[i][0]) * t, A[i][1] + (B[i][1] - A[i][1]) * t])
  }
  return ensureWinding(dedupe(out), true)
}

// Morph between two shape styles at the same nominal size. t is clamped to
// 0..1; the endpoints return the exact primitive outlines.
export function morphOutline(a: ShapeStyle, b: ShapeStyle, size: number, tol: number, t: number): Ring {
  const tt = Math.min(1, Math.max(0, t))
  if (tt <= 1e-9) return shapeOutline(a, size, tol)
  if (tt >= 1 - 1e-9) return shapeOutline(b, size, tol)
  return morphRings(shapeOutline(a, size, tol), shapeOutline(b, size, tol), tt)
}
